import React, { useState, useRef, useEffect } from "react";
import { sendMessage } from "./api";
import {
  Avatar, Box, IconButton, Paper, Typography,
  TextField, Button, useTheme, useMediaQuery
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import DownloadIcon from "@mui/icons-material/Download";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import TracyIcon from "./assets/tracy-icon.png";
import ChatResults from "./components/chat/ChatResults";


const SKIP_FIELDS = new Set([
  "_id", "__v",
  "InvoiceLines", "customers", "invoices",
  "CustomerSoapRequest", "ContactSoapRequest", "InvoiceSoapRequest",
  "BillToContactReferenceID", "BusinessProcessComments",
  "CurrencyReferenceID", "CustomerInvoiceHeaderMemo",
  "CustomerInvoiceTypeID", "CustomerPONumber", "CustomerReferenceID",
  "PaymentTermsReferenceID", "Submit", "TotalLines",
  "InvoiceMonth", "InvoiceYear",
  "CustomerMappingID", "CustomerMasterLoadID",
  "DateTimeLastUpdatedUTC", "DateTimeCreatedUTC",
]);

const FIELD_LABELS = {
  SourceSystemCode: "System Code",
  WorkdayCustomerBusinessEntityName: "Customer Name",
  totalCustomers: "Total Customers",
  totalContacts: "Total Contacts",
  totalInvoices: "Total Invoices",
  totalInvoicesAmount: "Total Invoice Amount ($)",
  TotalInvoiceAmount: "Invoice Amount ($)",
  CompanyReferenceID: "Company",
  InvoiceDate: "Invoice Date",
  Status: "Status",
  CustomerInvoiceReferenceID: "Invoice ID",
  SourceInvoiceReferenceID: "Source Invoice ID",
  CustomerInvoiceExtendedAmount: "Line Amount",
  CustomerInvoiceLineOrder: "Line Number",
  CustomerInvoiceLineItemDescription: "Description",
  CustomerInvoiceLineItemQuantity: "Quantity",
  CustomerInvoiceUnitCost: "Unit Cost",
  MappingFaultType: "Fault Type",
  FaultCode: "Fault Code",
  FaultString: "Fault Details",
  WorkdayCustomerID: "Workday Customer ID",
  SourceCustomerID: "Source Customer ID",
  WorkdayCustomerStatus: "Status",
  MappingType: "Type",
  totalFaults: "Total Faults",
  month: "Month",
  year: "Year",
  date: "Date",
  Month: "Month",
};

const DOLLAR_FIELDS = new Set([
  "totalInvoicesAmount",
  "TotalInvoiceAmount",
  "totalInvoiceAmount"
]);

function downloadCSV(dataArray) {
  if (!dataArray || dataArray.length === 0) return;

  const flatData = dataArray.map(item => {
    const res = {};
    Object.keys(item).forEach(k => {
      if (SKIP_FIELDS.has(k)) return;
      if (Array.isArray(item[k])) return;
      if (typeof item[k] === "object" && item[k] !== null) return;
      res[k] = item[k];
    });
    return res;
  });

  const headers = Array.from(new Set(flatData.flatMap(Object.keys)));
  const csvContent = [
    headers.join(","),
    ...flatData.map(row =>
      headers.map(f => `"${String(row[f] ?? "").replace(/"/g, '""')}"`).join(",")
    )
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = "Tracy_FinOps_Export.csv";
  link.click();
}

function getRecordFields(item) {
  const fields = [];
  Object.entries(item).forEach(([key, val]) => {
    if (SKIP_FIELDS.has(key)) return;
    if (val === null || val === undefined || val === "") return;
    if (Array.isArray(val)) return;
    if (typeof val === "object") return;

    const label = FIELD_LABELS[key] || key.replace(/([A-Z])/g, " $1").trim();
    let display = val;
    if (typeof val === "boolean") display = val ? "Yes" : "No";
    if (/date/i.test(key) && typeof val === "string" && /^\d{4}-\d{2}-\d{2}/.test(val) && !Number.isNaN(Date.parse(val))) {
      display = new Date(val).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" });
    }

    const isAmountField = DOLLAR_FIELDS.has(key) || /amount|revenue|value/i.test(label);
    if (isAmountField && !isNaN(val)) {
      display = `$${Number(val).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`;
    }

    fields.push({ key, label, display });
  });
  return fields;
}

const formatRecord = (item) => getRecordFields(item).map(({ label, display }) => `• ${label}: ${display}`).join("\n");

function formatResult(collectionName, result) {
  if (!result || result.length === 0) return "No data found.";

  const displayLimit = 10;
  const toDisplay = result.slice(0, displayLimit);

  if (toDisplay.length === 1) {
    return formatRecord(toDisplay[0]);
  }

  const lines = [];
  toDisplay.forEach((item, idx) => {
    lines.push(`#${idx + 1}`);
    lines.push(formatRecord(item));
    lines.push("");
  });

  if (result.length > displayLimit) {
    lines.push(`...and ${result.length - displayLimit} more records.`);
  }

  return lines.join("\n").trim();
}


// ── MAIN CHAT COMPONENT ────────────────────────────────────────────────────

export default function AIChat() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // ── Responsive breakpoints ────────────────────────────────────────────
  const isMobile = useMediaQuery("(max-width:600px)");
  const isSmallScreen = useMediaQuery("(max-width:480px)");

  // ── Theme-aware color tokens ──────────────────────────────────────────
  const accentBlue      = "#007bff";
  const accentBlueHover = "#0066ff";

  const windowBg      = isDark ? theme.palette.background.default  : "#ffffff";
  const messagesBg    = isDark ? theme.palette.background.alt      : "#f8fbff";
  const aiBubbleBg    = isDark ? theme.palette.background.alt      : "#ffffff";
  const inputAreaBg   = isDark ? theme.palette.background.default  : "#ffffff";
  const inputFieldBg  = isDark ? theme.palette.background.alt      : "#f8f9fa";

  const textPrimary   = isDark ? "#ffffff" : "#000000";
  const textSecondary = textPrimary;
  const textMuted     = isDark ? "#a6ceff" : "#075bb8";

  const windowBorder  = isDark ? "#2a4070"  : "#b3d4ff";
  const headerBorder  = isDark ? "#1a3060"  : "#cce4ff";
  const aiBubbleBorder= isDark ? "#2a4070"  : "#d0dfff";
  const inputDivider  = isDark ? theme.palette.divider : "#e0e0e0";

  // ── Responsive chat window dimensions ────────────────────────────────
  // On mobile: full screen minus small margin
  // On tablet/desktop: fixed comfortable size
  // Width: capped so it never overflows the viewport on any screen
  const chatWidth   = isSmallScreen ? "calc(100vw - 16px)"
                    : isMobile      ? "calc(100vw - 24px)"
                    : "min(480px, calc(100vw - 24px))";

  // Navbar height — must match your AppBar height exactly
  const navbarHeight = isMobile ? 56 : 64;

  // FAB size + gap above it
  const fabSize   = isMobile ? 56 : 68;
  const fabGap    = 8;  // px gap between FAB and bottom of chat window

  // Height: from just below navbar down to just above FAB
  // navbar → gap → chat window → gap → FAB → bottom margin
  const fabBottomPx = isMobile ? 12 : 20;
  // Top of chat window = navbar height + small gap
  const chatTop   = `${navbarHeight + 8}px`;

  // Bottom of chat window = above FAB
  const chatBottom  = `${fabBottomPx + fabSize + fabGap}px`;

  const chatRight   = "8px";  // safe on ALL screen sizes

  // FAB button — consistent safe margin
  const fabBottom = `${fabBottomPx}px`;
  const fabRight  = "8px";

  // ── State ─────────────────────────────────────────────────────────────
  const [open, setOpen]         = useState(false);
  const [input, setInput]       = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [conversationContext, setConversationContext] = useState({});
  const messagesEndRef          = useRef(null);
  const latestMessageRef        = useRef(null);
  const inputRef                = useRef(null);
  const loadingRef              = useRef(false);

  useEffect(() => {
    const last = messages[messages.length - 1];
    if (!loading && last?.type === "ai") latestMessageRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    else messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [messages, loading]);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{
        type: "ai",
        text: "Hello 👋 I'm Tracy, your FinOps AI assistant. How can I help you today?",
        suggestedPrompts: [
          "Search invoice by ID",
          "show overall summary",
          "show total customers",
          "show last invoices created",
          "show all system codes",
          "show recent invoice mapping faults"
        ],
        morePrompts: [
          "which system code has most customers",
          "which systemcode has most faults",
          "Show monthlyData for February 2026",
          "Show faults with email validation error",
          "Show master loads with PaymentTermsReferenceID Net_30",
          "Show invoice master loads with CurrencyReferenceID USD",
          "Show the single largest invoice by total amount",
          "Show yearData for all years",
          "show active customers",
          "show processed invoices",
          "which month has highest invoice amount in 2025"
        ],
        showAllPrompts: false
      }]);
    }
  }, [open, messages.length]);

  useEffect(() => {
    if (open && conversationContext.awaitingInvoiceId) inputRef.current?.focus();
  }, [open, conversationContext.awaitingInvoiceId]);

  // Close chat when pressing Escape key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape" && open) setOpen(false);
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [open]);

  const handleSend = async (overrideText = null, overrideContext = null) => {
    const textToSend = (typeof overrideText === "string" ? overrideText : input).trim();
    if (!textToSend || loadingRef.current) return;
    if (textToSend.length > 500) {
      setMessages(prev => [...prev, { type: "ai", text: "Please keep your question under 500 characters so I can search it accurately." }]);
      return;
    }

    setMessages(prev => [...prev, { type: "user", text: textToSend }]);
    if (typeof overrideText !== "string") setInput("");
    loadingRef.current = true;
    setLoading(true);
    const requestContext = overrideContext || conversationContext;

    try {
      const history = messages.slice(-6)
        .filter((message) => message.text)
        .map((message) => ({ role: message.type === "user" ? "user" : "assistant", content: (message.historyText || message.text).slice(0, 1200) }));
      const res = await sendMessage(textToSend, history, requestContext);
      const ai = res?.reply || { type: "text", data: "I couldn't read the response. Please try your question again." };
      let displayText = "";
      let rawDataForDownload = null;

      if (ai.type === "text") {
        displayText = typeof ai.data === "string" ? ai.data : "I couldn't format the response. Please try again.";
      } else if (ai.type === "data" && Array.isArray(ai.data)) {
        const label = ai.label ? `📋 ${ai.label}\n\n` : "";
        const sections = ai.description ? [ai.description] : [];

        ai.data.forEach(item => {
          if (item.error) {
            sections.push(`I couldn't load the ${item.collection || "requested"} results. Please narrow the search and try again.`);
            return;
          }
          if (!item.result || item.result.length === 0) {
            if (!ai.description) sections.push("No results found.");
            return;
          }

          rawDataForDownload = [...(rawDataForDownload || []), ...item.result];

          sections.push(formatResult(item.collection, item.result));
        });

        displayText = label + sections.join("\n\n");
      } else {
        displayText = JSON.stringify(ai, null, 2);
      }

      if (ai.context && typeof ai.context === "object") setConversationContext(ai.context);
      setMessages(prev => [...prev, {
        type: "ai",
        text: displayText.trim(),
        historyText: ai.type === "data" ? `${ai.label || "Search results"}. ${ai.description || ""}` : displayText.trim(),
        resultGroups: ai.type === "data" && Array.isArray(ai.data) ? ai.data : null,
        resultTitle: ai.label,
        resultDescription: ai.description,
        context: ai.context || requestContext,
        actions: Array.isArray(ai.actions)
          ? ai.actions.filter((action) => typeof action?.label === "string" && typeof action?.message === "string" && action.message.length <= 500).slice(0, 8)
          : [],
        rawData: rawDataForDownload,
        failed: Boolean(res?.failed),
        retryText: textToSend,
        retryContext: requestContext,
      }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        type: "ai",
        text: "I couldn't complete that request just now. Please try again.",
        failed: true,
        retryText: textToSend,
        retryContext: requestContext,
      }]);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  };

  // Typing-dots keyframe injected once
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      @keyframes typing {
        0%, 80%, 100% { transform: scale(0.3); opacity: 0.4; }
        40% { transform: scale(1); opacity: 1; }
      }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  // ── Prompt button shared sx ───────────────────────────────────────────
  const promptButtonSx = {
    textTransform: "none",
    justifyContent: "center",
    textAlign: "center",
    borderRadius: "12px",
    borderColor: accentBlue,
    color: accentBlue,
    width: "100%",
    fontSize: isMobile ? "0.78rem" : "0.85rem",
    "&:hover": {
      bgcolor: accentBlue,
      color: "white",
      borderColor: accentBlue
    },
    "&.Mui-disabled": { color: isDark ? "#a6ceff" : "#075bb8", borderColor: isDark ? "#425875" : "#c9d7e7", opacity: 1 },
    "&.Mui-focusVisible": { outline: `2px solid ${accentBlue}`, outlineOffset: 2 },
  };

  return (
    <>
      {/* ── Floating Button ────────────────────────────────────────────── */}
      <IconButton
        aria-label={open ? "Close Tracy chat" : "Open Tracy chat"}
        onClick={() => setOpen(!open)}
        sx={{
          position: "fixed",
          bottom: fabBottom,
          right: fabRight,
          zIndex: 10000,
          bgcolor: isDark ? "#1e2a3a" : "#eaf3ff",
          color: "white",
          "&:hover": {
            bgcolor: isDark ? "#263447" : "#dcecff",
            transform: "scale(1.08)"
          },
          width: isMobile ? 56 : 68,
          height: isMobile ? 56 : 68,
          borderRadius: "18px"
        }}
      >
        <Avatar
          src={TracyIcon}
          sx={{ width: isMobile ? 42 : 52, height: isMobile ? 42 : 52 }}
        />
      </IconButton>

      {/* ── Chat Window ────────────────────────────────────────────────── */}
      {open && (
        <Paper
          role="dialog"
          aria-label="Tracy assistant"
          elevation={10}
          sx={{
            position: "fixed",
            bottom: chatBottom,
            top: chatTop,       // ← never overlap the navbar
            right: chatRight,
            width: chatWidth,
            height: "auto",     // let top+bottom define the height
            maxWidth: "calc(100vw - 16px)",
            maxHeight: `calc(100svh - ${isMobile ? 56 : 64}px - 16px)`,
            display: "flex",
            flexDirection: "column",
            borderRadius: isSmallScreen ? "16px" : "22px",
            overflow: "hidden",
            zIndex: 9999,
            boxShadow: isDark
              ? "0 20px 65px rgba(0,0,0,0.55)"
              : "0 20px 65px rgba(0,0,0,0.25)",
            border: `1px solid ${windowBorder}`,
            background: windowBg
          }}
        >
          {/* ── Header ─────────────────────────────────────────────────── */}
          <Box sx={{
            display: "flex",
            alignItems: "center",
            p: "14px 16px",
            bgcolor: accentBlue,
            color: "white",
            borderBottom: `2px solid ${headerBorder}`,
            flexShrink: 0,
          }}>
            <Avatar
              src={TracyIcon}
              sx={{ mr: 1.25, width: 40, height: 40 }}
            />
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography
                variant={isMobile ? "body1" : "h6"}
                fontWeight={600}
                noWrap
              >
                Tracy - FinOps AI Agent
              </Typography>
              <Typography variant="caption" sx={{ color: "#ecf5ff", fontSize: 11 }}>
                <span style={{ color: "#8ef0bd" }}>●</span> Your financial operations assistant
              </Typography>
            </Box>
            <IconButton
              aria-label="Start new chat"
              title="Start new chat"
              disabled={loading}
              onClick={() => { setMessages([]); setInput(""); setConversationContext({}); }}
              sx={{ color: "white" }}
            >
              <RestartAltIcon />
            </IconButton>
            <IconButton
              aria-label="Close chat"
              onClick={() => setOpen(false)}
              sx={{ color: "white", ml: 1, flexShrink: 0 }}
              size={isMobile ? "small" : "medium"}
            >
              ✕
            </IconButton>
          </Box>

          {/* ── Messages Area ───────────────────────────────────────────── */}
          <Box sx={{
            flex: 1,
            p: isMobile ? "12px 8px" : "16px 12px",
            overflowY: "auto",
            overflowX: "hidden",
            bgcolor: messagesBg,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            minHeight: 0,
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              background: isDark ? "#3a5080" : "#c0d0e8",
              borderRadius: "3px"
            }
          }}>
            {messages.map((msg, idx) => (
              <Box
                key={idx}
                ref={idx === messages.length - 1 ? latestMessageRef : null}
                sx={{
                  display: "flex",
                  flexDirection: msg.type === "user" ? "row-reverse" : "row",
                  alignItems: "flex-start",
                  gap: 0.8,
                  minWidth: 0,
                }}
              >
                {msg.type === "ai" && (
                  <Avatar
                    src={TracyIcon}
                    sx={{ width: 28, height: 28, mt: 0.5, flexShrink: 0 }}
                  />
                )}

                <Box
                  sx={{
                    maxWidth: msg.type === "user" ? "85%" : "100%",
                    flex: msg.type === "ai" ? 1 : undefined,
                    minWidth: 0,
                    ml: msg.type === "user" ? 2 : 0,
                    mr: msg.type === "ai"  ? 0.5 : 0
                  }}
                >
                  <Paper sx={{
                    p: isMobile ? "14px 12px" : "16px",
                    borderRadius: msg.type === "user"
                      ? "20px 20px 6px 20px"
                      : "20px 20px 20px 6px",
                    bgcolor: msg.type === "user" ? accentBlue : msg.resultGroups ? (isDark ? "#132238" : "#f6f9fe") : aiBubbleBg,
                    color: msg.type === "user" ? "white" : textPrimary,
                    boxShadow: "0 2px 8px rgba(15,40,75,0.04)",
                    border: msg.type === "ai"
                      ? `1px solid ${aiBubbleBorder}`
                      : "none",
                  }}>
                    {msg.resultGroups ? <ChatResults
                      title={msg.resultTitle} description={msg.resultDescription} groups={msg.resultGroups}
                      formatFields={getRecordFields} busy={loading}
                      onInspect={(message) => handleSend(message, msg.context)}
                    /> : <Box sx={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      lineHeight: 1.65,
                      fontSize: isMobile ? "0.85rem" : "0.94rem"
                    }}>
                      {msg.text}
                    </Box>}

                    {/* ── Suggested Prompts ─────────────────────────────── */}
                    {msg.suggestedPrompts && (
                      <Box sx={{
                        mt: 2,
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.8,
                        alignItems: "center"
                      }}>
                        {msg.suggestedPrompts.map((prompt, i) => (
                          <Button
                            key={i}
                            variant="outlined"
                            size="small"
                            fullWidth
                            onClick={() => handleSend(prompt)}
                            disabled={loading}
                            sx={promptButtonSx}
                          >
                            {prompt}
                          </Button>
                        ))}

                        {msg.morePrompts && (
                          <Button
                            variant="text"
                            size="small"
                            sx={{
                              textTransform: "none",
                              color: isDark ? "#66aaff" : "#0066ff",
                              fontWeight: 500,
                              mt: 0.5,
                              alignSelf: "center",
                              fontSize: isMobile ? "0.78rem" : "0.85rem"
                            }}
                            onClick={() => {
                              setMessages(prev =>
                                prev.map(m =>
                                  m.morePrompts
                                    ? { ...m, showAllPrompts: !m.showAllPrompts }
                                    : m
                                )
                              );
                            }}
                          >
                            📋 {msg.showAllPrompts ? "Show Less Questions" : "Show More Questions"}
                          </Button>
                        )}

                        {msg.showAllPrompts && msg.morePrompts && (
                          <Box sx={{
                            mt: 0.5,
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.8,
                            alignItems: "center",
                            width: "100%"
                          }}>
                            {msg.morePrompts.map((prompt, i) => (
                              <Button
                                key={i}
                                variant="outlined"
                                size="small"
                                fullWidth
                                onClick={() => handleSend(prompt)}
                                disabled={loading}
                                sx={{ ...promptButtonSx, maxWidth: "92%", width: "auto" }}
                              >
                                {prompt}
                              </Button>
                            ))}
                          </Box>
                        )}

                        <Button
                          variant="text"
                          size="small"
                          sx={{
                            textTransform: "none",
                            color: textMuted,
                            mt: 0.5,
                            fontSize: isMobile ? "0.75rem" : "0.82rem"
                          }}
                          onClick={() => inputRef.current?.focus()}
                        >
                          ✏️ Type your own question below...
                        </Button>
                      </Box>
                    )}

                    {/* ── CSV Download ──────────────────────────────────── */}
                    {msg.type === "ai" && msg.actions?.length > 0 && (
                      <Box sx={{ mt: 2, pt: 1.5, borderTop: `1px solid ${inputDivider}` }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: textMuted, mb: 1 }}>
                          {msg.context?.invoiceId ? "Explore this invoice" : "What would you like to do next?"}
                        </Typography>
                        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 0.75 }}>
                        {msg.actions.map((action, actionIndex) => (
                          <Button
                            key={`${action.label}-${actionIndex}`}
                            variant="outlined"
                            size="small"
                            disabled={loading}
                            onClick={() => handleSend(action.message, msg.context)}
                            sx={{
                              textTransform: "none", borderRadius: "8px", fontSize: 12, fontWeight: 600,
                              minWidth: 0, minHeight: 38, py: 0.75, lineHeight: 1.5,
                              color: isDark ? "#a6ceff" : "#075bb8", borderColor: isDark ? "#415d80" : "#bdd4f0",
                              bgcolor: isDark ? "#1d324f" : "#edf5ff", overflowWrap: "anywhere",
                              "&:hover": { bgcolor: isDark ? "#29476d" : "#dfeeff", borderColor: isDark ? "#86baff" : "#075bb8" },
                              "&.Mui-disabled": { color: isDark ? "#a6ceff" : "#075bb8", borderColor: isDark ? "#425875" : "#c9d7e7", bgcolor: isDark ? "#1b2c44" : "#edf5ff", opacity: 1 },
                              "&.Mui-focusVisible": { outline: `2px solid ${accentBlue}`, outlineOffset: 2 },
                            }}
                          >
                            {action.label}
                          </Button>
                        ))}
                        </Box>
                      </Box>
                    )}
                    {msg.type === "ai" && msg.rawData && (
                      <Button
                        variant="outlined"
                        fullWidth
                        startIcon={<DownloadIcon />}
                        onClick={() => downloadCSV(msg.rawData)}
                        size={isMobile ? "small" : "medium"}
                        sx={{
                          mt: 2,
                          color: isDark ? "#a6ceff" : "#075bb8",
                          borderColor: isDark ? "#415d80" : "#bdd4f0",
                          "&:hover": { bgcolor: isDark ? "#1d324f" : "#edf5ff", borderColor: accentBlue },
                          textTransform: "none",
                          borderRadius: "8px",
                          py: isMobile ? 0.6 : 1,
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        Download CSV
                      </Button>
                    )}
                    {msg.type === "ai" && msg.failed && (
                      <Button
                        size="small"
                        onClick={() => handleSend(msg.retryText, msg.retryContext)}
                        disabled={loading}
                        sx={{ mt: 1, textTransform: "none", color: isDark ? "#a6ceff" : "#075bb8", "&.Mui-disabled": { color: isDark ? "#a6ceff" : "#075bb8" } }}
                      >
                        Try again
                      </Button>
                    )}
                  </Paper>
                </Box>
              </Box>
            ))}

            {/* ── Typing indicator ──────────────────────────────────────── */}
            {loading && (
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <Avatar
                  src={TracyIcon}
                  sx={{ width: isMobile ? 32 : 42, height: isMobile ? 32 : 42, flexShrink: 0 }}
                />
                <Paper sx={{
                  p: isMobile ? "0.6rem 1rem" : "1rem 1.2rem",
                  borderRadius: "20px 20px 20px 6px",
                  bgcolor: aiBubbleBg,
                  border: `1px solid ${aiBubbleBorder}`,
                  boxShadow: isDark
                    ? "0 4px 15px rgba(0,0,0,0.35)"
                    : "0 4px 15px rgba(0,0,0,0.08)"
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Typography sx={{ color: textSecondary, fontSize: isMobile ? "0.82rem" : "0.95rem" }}>
                      Tracy is typing
                    </Typography>
                    <Box sx={{ display: "flex", gap: "5px" }}>
                      {[0, 0.2, 0.4].map((delay, i) => (
                        <Box
                          key={i}
                          sx={{
                            width: 7, height: 7,
                            bgcolor: accentBlue,
                            borderRadius: "50%",
                            animation: `typing 1.4s infinite ease-in-out ${delay}s`,
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                </Paper>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </Box>

          {/* ── Input Area ──────────────────────────────────────────────── */}
          <Box sx={{
            p: "12px",
            bgcolor: inputAreaBg,
            borderTop: `1px solid ${inputDivider}`,
            flexShrink: 0,
          }}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                inputRef={inputRef}
                fullWidth
                variant="outlined"
                placeholder={conversationContext.awaitingInvoiceId ? "Enter invoice ID…" : "Ask Tracy…"}
                value={input}
                onChange={(e) => setInput(e.target.value.slice(0, 500))}
                inputProps={{ maxLength: 500, "aria-label": "Message Tracy" }}
                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSend()}
                size={isMobile ? "small" : "medium"}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "14px",
                    backgroundColor: inputFieldBg,
                    color: textPrimary,
                    fontSize: isMobile ? "0.85rem" : "0.95rem",
                    "& fieldset": {
                      borderColor: isDark ? "#2a4070" : "#d0dfff"
                    },
                    "&:hover fieldset": {
                      borderColor: isDark ? "#4a70b0" : "#007bff"
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: accentBlue
                    }
                  },
                  "& .MuiOutlinedInput-input::placeholder": {
                    color: textMuted,
                    opacity: 1
                  },
                  "& .MuiOutlinedInput-input": {
                    color: textPrimary
                  }
                }}
              />
              <IconButton
                aria-label="Send message"
                onClick={() => handleSend()}
                disabled={!input.trim() || loading}
                sx={{
                  bgcolor: accentBlue,
                  color: "white",
                  "&:hover": { bgcolor: accentBlueHover },
                  "&.Mui-disabled": {
                    bgcolor: isDark ? "#1a3060" : "#b3d4ff",
                    color: isDark ? "#4a6090" : "#ffffff"
                  },
                  width: isMobile ? 40 : 52,
                  height: isMobile ? 40 : 52,
                  borderRadius: "14px",
                  flexShrink: 0,
                  alignSelf: "center",
                }}
              >
                <SendIcon fontSize={isMobile ? "small" : "medium"} />
              </IconButton>
            </Box>
          </Box>
        </Paper>
      )}
    </>
  );
}
